<template>
  <div class="w-100">
    <template v-if="player">
      <div class="headline d-flex flex-row align-items-center justify-content-between w-100 border-bottom pb-3">
        <h2 class="card-title font-weight-bolder">{{ $t('title.game_history') }}</h2>
      </div>
      <div class="row mt-4">
        <div class="col-12">
          <div class="questions sygni-grid">
            <div class="">
              <div class="filters">

                <div class="row">
                  <div class="col-4">
                    <filter-date-range-picker
                        :filter-query="filterQuery"
                        :locale="{firstDay: 1, format: 'yyyy-mm-dd'}"
                        :auto="true"
                        :linked="false"
                        :time-picker="true"
                        filter-name="startedAt"
                        open-direction="left"
                        :label="$t('filters.label.game_date')">
                    </filter-date-range-picker>
                  </div>
                  <div class="col-4">

                    <filter-input-with-operator
                        :filter-query="filterQuery"
                        :operator-options="selectOptions.comparisonOperator"
                        :operator-multiple="false"
                        :default-operator="{value: 'eq', text: 'equales'}"
                        :restrict-input="true"
                        restriction-type="decimals"
                        filter-name="buyIn"
                        :label="$t('filters.label.buy_in')">
                    </filter-input-with-operator>

                  </div>
                  <div class="col-4">
                    <filter-select
                        :filter-query="filterQuery"
                        :options="selectOptions.walletCurrency"
                        :multiple="false"
                        options-label="text"
                        options-track-by="value"
                        filter-name="currency"
                        :label="$t('filters.label.wallet_currency')"
                        :placeholder="$t('filters.placeholder.wallet_currency')">
                    </filter-select>
                  </div>
                </div>
                <div class="row">
                  <div class="col-4">
                    <filter-select
                        :filter-query="filterQuery"
                        :options="selectOptions.position"
                        :multiple="false"
                        options-label="text"
                        options-track-by="value"
                        filter-name="playerPosition"
                        :label="$t('filters.label.position')"
                        :placeholder="$t('filters.label.placeholder')">
                    </filter-select>
                  </div>
                  <div class="col-4">

                    <filter-input-with-operator
                        :filter-query="filterQuery"
                        :operator-options="selectOptions.comparisonOperator"
                        :operator-multiple="false"
                        :default-operator="{value: 'eq', text: 'equales'}"
                        :restrict-input="true"
                        restriction-type="decimals"
                        filter-name="playerAward"
                        :label="$t('filters.label.prize')">
                    </filter-input-with-operator>

                  </div>
                  <div class="col-4">
                    <filter-input
                        :filter-query="filterQuery"
                        :default-filter="gameId"
                        filter-name="gameId"
                        filter-operator="like"
                        :label="$t('filters.label.game_id')">
                    </filter-input>
                  </div>
                </div>

                <div class="row">
                  <div class="col-4">
                    <filter-select
                        :filter-query="filterQuery"
                        :options="selectOptions.status"
                        :multiple="false"
                        options-label="text"
                        options-track-by="value"
                        filter-name="status"
                        :label="$t('filters.label.status')"
                        :placeholder="$t('filters.placeholder.status')">
                    </filter-select>
                  </div>
                </div>

                <div class="row">
                  <div class="col-12">
                    <div class="float-right mt-5">
                      <b-button @click="resetFilters()" variant="secondary" class="mr-2">{{$t('filters.btn.clear')}}</b-button>
                      <b-button @click="applyFilters()" variant="primary">{{$t('filters.btn.apply')}}</b-button>
                    </div>
                  </div>
                </div>
              </div> <!-- #filters-->
              <div class="row mt-2">
                <div class="col-12">
                  <grid-pagination :total-count="totalCount"
                                   :per-page="perPage"
                                   :current-page="currentPage"
                                   @load-items="paginationReload"></grid-pagination>

                  <div class="table-responsive">
                    <b-table striped show-empty
                             :empty-text="$t('table.no_results')"
                             :busy="isBusy"
                             hover
                             head-variant="dark"
                             outlined
                             :sort-by.sync="orderColumn"
                             :sort-desc.sync="orderDirection"
                             :no-local-sorting="true"
                             :items="items"
                             :fields="fields">

                      <template #cell(gameId)="data">
                        <div>{{ data.value | shortId }}</div>
                      </template>

                      <template #cell(currency)="data">
                        <div>{{ data.item.demoGame ? 'Demo' : $t(data.value) }}</div>
                      </template>

                      <template #cell(startedAt)="data">
                        <div>{{ data.value | dateFormat('YYYY-MM-DD HH:mm:ss') }}</div>
                      </template>

                      <template #cell(position)="data">
                        <div>{{ data.item | playerPosition(player) }}</div>
                      </template>

                      <template #cell(buyIn)="data">
                        <div>{{ data.item.demoGame ? 0 : (data.value | numberFormat) }}</div>
                      </template>

                      <template #cell(playerAward)="data">
                        <div>{{ data.item.demoGame ? 0 : (data.value | numberFormat) }}</div>
                      </template>

                      <template #cell(status)="data">
                        <b-badge pill variant="primary" v-if="data.value === 'CREATED'">{{$t(data.value)}}</b-badge>
                        <b-badge pill variant="warning" v-if="data.value === 'COUNTDOWN_STARTED'">{{$t(data.value)}}</b-badge>
                        <b-badge pill variant="info" v-if="data.value === 'STARTED'">{{$t(data.value)}}</b-badge>
                        <b-badge pill variant="success" v-if="data.value === 'FINISHED'">{{$t(data.value)}}</b-badge>
                        <b-badge pill variant="danger" v-if="data.value === 'CANCELLED'">{{$t(data.value)}}</b-badge>
                      </template>

                      <template #cell(actions)="data">
                        <b-button variant="primary" size="sm"
                                  @click="showItemDetails(data.item)">
                          {{ $t('table.btn.show') }}
                        </b-button>
                      </template>

                      <template #table-busy>
                        <div class="text-center text-danger my-2">
                          <b-spinner class="align-middle"></b-spinner>
                          <strong>{{$t('state.loading')}}</strong>
                        </div>
                      </template>

                    </b-table>
                  </div> <!-- #table-responsive-->

                  <grid-pagination :total-count="totalCount"
                                   :per-page="perPage"
                                   :current-page="currentPage"
                                   @load-items="paginationReload"></grid-pagination>

                </div>
              </div>
            </div> <!-- #card-body-->
          </div> <!-- #card questions sygni-grid-->
        </div>
      </div>
    </template>
    <div class="spinner-wrapper" v-else>
      <b-spinner></b-spinner>
    </div>
  </div>

</template>

<script>
import GridMixin from "../../../components/grid/GridMixin";
import FilterSelect from "../../../components/grid/filters/FilterSelect";
import FilterInput from "../../../components/grid/filters/FilterInput";
import FilterInputWithOperator from "../../../components/grid/filters/FilterInputWithOperator";
import FilterDateRangePicker from "../../../components/grid/filters/FilterDateRangePicker";
import GridPagination from "../../../components/grid/grid-pagination/GridPagination";
import Vue from "vue";
import GameDetailsModal from "./GameDetailsModal";

export default {
  mixins: [GridMixin],
  components: {
    FilterSelect,
    FilterInput,
    FilterInputWithOperator,
    FilterDateRangePicker,
    GridPagination
  },
  data() {
    return {
      gameId: null,
      gridGetUrl: null,
      wallets: null,
      walletCurrency: null,
      orderColumn: 'startedAt',
      fields: [
        {key: 'gameId', label: this.$t('table.header.game_id')},
        {key: 'currency', label: this.$t('table.header.currency')},
        {key: 'buyIn', label: this.$t('table.header.buy_in'), sortable: true},
        {key: 'startedAt', label: this.$t('table.header.created_at'), sortable: true},
        {key: 'playerPosition', label: this.$t('table.header.position'), sortable: true},
        {key: 'numberPlayers', label: this.$t('table.header.max_players')},
        {key: 'playerAward', label: this.$t('table.header.prize'), sortable: true},
        {key: 'status', label: this.$t('table.header.status'), sortable: true},
        {key: 'actions', label: this.$t('table.header.actions'),  sortable: false}
      ],
      items: [],
      selectOptions: {
        status: [
          {value: 'FINISHED', text: this.$t('FINISHED')},
          {value: 'CANCELLED', text: this.$t('CANCELLED')},
          {value: 'COUNTDOWN_STARTED', text: this.$t('COUNTDOWN_STARTED')},
          {value: 'STARTED', text: this.$t('STARTED')},
          {value: 'CREATED', text: this.$t('CREATED')},
        ],
        walletCurrency: [
          {value: 'FUN_COINS', text: this.$t('FUN_COINS')},
          {value: 'QUIZ_COINS', text: this.$t('QUIZ_COINS')},
        ],
        position: [
          {value: 1, text: this.$t('option.first')},
          {value: 2, text: this.$t('option.second')},
          {value: 3, text: this.$t('option.third')},
          {value: 4, text: this.$t('option.fourth')},
        ],
        comparisonOperator: [
          {value: 'eq', text: this.$t('operator.equales')},
          {value: 'gte', text: this.$t('operator.greater_then')},
          {value: 'lte', text: this.$t('operator.lower_than')},
          {value: 'btw', text: this.$t('operator.between')},
        ]
      },
    };
  },
  created() {
    if(this.$route.query.gameId) {
      this.gameId = this.$route.query.gameId;
    }
  },
  mounted() {
    if (this.player) {
      setTimeout(() => this.getHistory(), 10)
    }
  },
  methods: {
    changeCurrency($e) {
      this.getWallets($e.value);
    },
    getHistory() {
      const playerIdFromUrl = this.$route.params.id;

      if (this.player) {
        this.gridGetUrl = `/api/player/${playerIdFromUrl}/game-history-item`;
        this.loadItems();
      }
    },
    showItemDetails(item) {
      const
          ModalConstructor = Vue.extend(GameDetailsModal),
          modal = new ModalConstructor({parent: this});

      modal.name = 'game-details';
      modal.title = 'Game details';
      modal.game = {...item};

      modal.$mount();
    }
  },
  watch: {
    player: function (n, o) {
      if (!o) {
        setTimeout(() => this.getHistory(), 10);
      }
    }
  },
  computed: {
    player: function () {
      return this.$store.getters["players/player"];
    },
    apiKey: function () {
      return this.$store.getters["root/apiKey"];
    },
  }
}
</script>
